// ────────────────────────────────────────────────────────────────────
// DockWindow — chrome shared by every dock window (MODS, customer chat,
// ...). Deliberately plain: hairline border, mono uppercase title, a
// minimize (–) and close (×) control. No rounded chat-heads, no avatars —
// this is instrument-panel styling, not a social chat widget. Chrome
// classes are copied from ChatDock's existing `<aside>` header so the two
// docks read as one system once 1b migrates them in.
// ────────────────────────────────────────────────────────────────────
import type { KeyboardEvent, ReactNode } from "react";

export interface DockWindowProps {
  id: string;
  title: string;
  icon?: ReactNode;
  /** Small node rendered right after the title (e.g. MODS's PREMIUM badge). */
  badge?: ReactNode;
  body: ReactNode;
  onMinimize: () => void;
  onClose: () => void;
}

export function DockWindow({ id, title, icon, badge, body, onMinimize, onClose }: DockWindowProps): JSX.Element {
  // Esc minimizes (not closes) — matches a real window manager and never
  // discards state, which closing would. stopPropagation so Esc inside a
  // nested input (e.g. clearing its own value) doesn't also bubble here
  // when the input handles it itself; harmless either way if it does.
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onMinimize();
    }
  };

  return (
    <div
      data-dock-window={id}
      onKeyDown={handleKeyDown}
      className="flex flex-col w-[calc(100vw-1.5rem)] sm:w-[300px] h-[440px] max-h-[70vh] border border-panel-700 bg-panel-900 shadow-raised"
    >
      <div className="flex items-center gap-2 px-3 h-10 border-b border-panel-700 shrink-0">
        {icon}
        <span className="min-w-0 truncate font-mono text-xs uppercase tracking-wider text-ink-2">
          {title}
        </span>
        {badge}
        <span className="flex-1" />
        <button
          type="button"
          onClick={onMinimize}
          aria-label="ย่อ"
          title="ย่อ"
          className="flex items-center justify-center w-5 h-5 text-ink-3 hover:text-ink-1 font-mono text-sm leading-none"
        >
          –
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="ปิด"
          title="ปิด"
          className="flex items-center justify-center w-5 h-5 text-ink-3 hover:text-ink-1 font-mono text-sm leading-none"
        >
          ×
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
        {body}
      </div>
    </div>
  );
}
